import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import MonitorIcon from '@mui/icons-material/Monitor';
import FlagIcon from '@mui/icons-material/Flag';
import ChatIcon from '@mui/icons-material/Chat';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useAvatar } from './AvatarContext';
import GuidedTour, {
  TourInvite,
  clearTourOfferPending,
  isTourOfferPending,
} from './GuidedTour';
import AvatarPicker, {
  applyAvatar,
  hasChosenAvatarLocally,
  markAvatarChosenLocally,
} from './AvatarPicker';
import {
  CUSTOM_AVATAR_ID,
  findAvatarById,
  readStoredAvatar,
  storeCustomAvatarUrl,
  storeSelectedAvatar,
} from './avatarCatalog';
import config from './config';

// How long to wait for the user record before skipping the avatar step for
// this visit (a cold backend must not hold up Home).
const AVATAR_CHECK_TIMEOUT_MS = 8000;

// Home is a simple launcher: four large cards, one per area of the app. The
// detail for each area lives on its own page so the caregiver never faces a
// crowded screen. Tapping a card (anywhere but Open) has the assistant explain it
// aloud, so the caregiver chooses what gets read to them.
const HOME_SECTIONS = [
  {
    title: 'Respite',
    description: 'Record the breaks you take from caregiving, and get help finding time to rest.',
    spokenIntro:
      "Respite is your time away from caregiving. Here you can record the breaks you take, see whether you've had at least eight hours of rest in a row this week, and get help finding ways to make that time for yourself.",
    icon: SelfImprovementIcon,
    route: '/respite-record',
  },
  {
    title: 'HABC Monitor',
    description: 'Every two weeks, check in on memory, daily activities, mood, and your own stress.',
    spokenIntro:
      "The HABC Monitor is a short check-up you complete about every two weeks. It asks about your loved one's memory, daily activities, mood and behavior, and about your own stress, so we can see how things are changing and point you to the right support.",
    icon: MonitorIcon,
    route: '/habc-monitor',
  },
  {
    title: 'My Two-Week Focus',
    description: 'The suggestions you chose to try in your conversations, and how each one is going.',
    spokenIntro:
      "My Two-Week Focus shows the suggestions you chose to try after our conversations. You can see which one you're working on right now, how it's going, and open the conversation it came from.",
    icon: FlagIcon,
    route: '/focus',
  },
  {
    title: 'Care Sessions',
    description: 'Talk with {name} about a concern, or pick up an earlier conversation.',
    spokenIntro:
      "Care Sessions is where you and I talk. You can start a new conversation about anything that's on your mind, or pick up an earlier one right where we left off.",
    icon: ChatIcon,
    route: '/sessions',
  },
];

// Steps for the guided tour: the left side (assistant, status, mic, menu), then
// each card on the right. Card steps reuse the cards' spoken introductions.
// "{name}" in card and tour text becomes the selected avatar's name.
const withAssistantName = (text, name) =>
  typeof text === 'string' ? text.replace(/\{name\}/g, name) : text;

const TOUR_STEPS = [
  {
    target: '[data-tour="avatar"]',
    title: 'This is me, {name}',
    text: "I'm your care assistant. I'll stay right here on every screen, and I can read things out and talk with you about caregiving. Tap Next to continue, or Exit at any time.",
  },
  {
    target: '[data-tour="status"]',
    title: 'What I am doing',
    text: 'This shows what I am doing right now: Ready when I am waiting, Listening when the microphone is on, Thinking while I work on an answer, and Speaking while I talk.',
  },
  // The mic button is hidden for now (see AppShell.js); restore with it.
  // {
  //   target: '[data-tour="mute"]',
  //   title: 'The microphone switch',
  //   text: 'This is the microphone switch. When it says Mic on, I listen after I finish speaking so you can simply answer. Tap it to mute me whenever you want some privacy, and tap again to turn it back on.',
  // },
  {
    target: '[data-tour="nav"]',
    title: 'The menu',
    text: 'On the far left is the menu. The house brings you back to this home screen. The other buttons open Care Sessions, the HABC Monitor, Respite, your settings, and log out. Tap the small arrow at the top to see their names.',
  },
  ...HOME_SECTIONS.map((section) => ({
    target: `[data-tour="${section.route}"]`,
    title: section.title,
    text: section.spokenIntro,
  })),
  {
    target: null,
    title: "That's everything",
    text: "That's everything. Tap any card to hear about it again, or tap Open to go there. I'm here whenever you need me.",
  },
];

// The offer appears once per login. After this many "No, thanks" in a row
// (i.e. on consecutive logins) it stops appearing; the Guidance button still
// starts the tour, and a "Yes" resets the count.
const TOUR_DECLINE_LIMIT = 3;
const TOUR_DECLINES_KEY = 'vicaTourDeclines';

const readTourDeclines = () => {
  try {
    return Number(localStorage.getItem(TOUR_DECLINES_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeTourDeclines = (count) => {
  try {
    localStorage.setItem(TOUR_DECLINES_KEY, String(count));
  } catch {
    // storage unavailable; the offer simply keeps appearing
  }
};

function Home() {
  const navigate = useNavigate();
  const { speakText, stopSpeaking, assistantName } = useAvatar();
  const tourSteps = useMemo(
    () =>
      TOUR_STEPS.map((step) => ({
        ...step,
        title: withAssistantName(step.title, assistantName),
        text: withAssistantName(step.text, assistantName),
      })),
    [assistantName]
  );
  // 'invite' -> ask; 'tour' -> walking through; 'idle' -> nothing shown.
  // The offer is made once per login (see isTourOfferPending), until it has
  // been declined TOUR_DECLINE_LIMIT times in a row.
  const [tourMode, setTourMode] = useState(() =>
    isTourOfferPending() && readTourDeclines() < TOUR_DECLINE_LIMIT ? 'invite' : 'idle'
  );
  // First login: the caregiver picks an assistant before the tour invite.
  // 'checking' -> asking the backend whether an avatar was ever chosen;
  // 'choose' -> picker shown; 'done' -> carry on (tour invite may show).
  const [avatarStep, setAvatarStep] = useState(() =>
    hasChosenAvatarLocally(localStorage.getItem('userId')) ? 'done' : 'checking'
  );

  useEffect(() => {
    if (avatarStep !== 'checking') {
      return undefined;
    }
    const userId = localStorage.getItem('userId');
    if (!userId) {
      setAvatarStep('done');
      return undefined;
    }
    let canceled = false;
    axios
      .get(`${config.API_BASE_URL}/user-avatar`, {
        params: { userId },
        timeout: AVATAR_CHECK_TIMEOUT_MS,
      })
      .then((res) => {
        if (canceled) {
          return;
        }
        const savedId = typeof res.data?.selectedAvatar === 'string' ? res.data.selectedAvatar : '';
        const customUrl = typeof res.data?.avatarUrl === 'string' ? res.data.avatarUrl : '';
        const saved = findAvatarById(savedId);
        const savedCustom = savedId === CUSTOM_AVATAR_ID && Boolean(customUrl);
        if (!saved && !savedCustom) {
          setAvatarStep('choose');
          return;
        }
        // Chosen before (maybe on another device): use that assistant here too.
        markAvatarChosenLocally(userId);
        if (savedCustom) {
          storeCustomAvatarUrl(customUrl);
          if (readStoredAvatar() !== CUSTOM_AVATAR_ID) {
            storeSelectedAvatar(CUSTOM_AVATAR_ID);
          }
        } else if (readStoredAvatar() !== saved.id) {
          applyAvatar(saved);
        }
        setAvatarStep('done');
      })
      .catch((error) => {
        console.warn('Could not check the chosen avatar; skipping for now.', error);
        if (!canceled) {
          setAvatarStep('done');
        }
      });
    return () => {
      canceled = true;
    };
  }, [avatarStep]);

  const explain = (section) => {
    stopSpeaking();
    speakText(section.spokenIntro);
  };

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* While the assistant picker is open it replaces the cards (no dimmed
          overlay), so the page looks like any other screen. */}
      {avatarStep !== 'choose' && (
        <main className="h-full overflow-auto p-4 md:p-7">
          <div className="mx-auto flex h-full max-w-[1300px] flex-col justify-center gap-4">
            <div className="flex justify-end">
              <button
                type="button"
                className="vica-guidance-button"
                onClick={() => setTourMode('tour')}
              >
                <VolumeUpIcon sx={{ fontSize: 28 }} />
                Guidance For This Page
              </button>
            </div>

            {HOME_SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.route}
                  role="button"
                  tabIndex={0}
                  className="vica-surface vica-home-card flex items-center gap-5 px-6 py-5 md:px-8 md:py-6"
                  data-tour={section.route}
                  onClick={() => explain(section)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      explain(section);
                    }
                  }}
                  title={`Hear about ${section.title}`}
                >
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#d9eef9] text-[#0f4e71]">
                    <Icon sx={{ fontSize: 36 }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="vica-title">{section.title}</h2>
                    <p className="vica-subtitle mt-1 !text-[1.3rem] leading-8">{withAssistantName(section.description, assistantName)}</p>
                  </div>
                  <button
                    type="button"
                    className="vica-btn shrink-0 !px-8 !py-3.5 !text-[1.15rem]"
                    onClick={(event) => {
                      event.stopPropagation();
                      stopSpeaking();
                      navigate(section.route);
                    }}
                  >
                    Open
                  </button>
                </div>
              );
            })}
          </div>
        </main>
      )}

      {avatarStep === 'choose' && <AvatarPicker onDone={() => setAvatarStep('done')} />}
      {avatarStep === 'done' && tourMode === 'invite' && (
        <TourInvite
          onAccept={() => {
            clearTourOfferPending();
            writeTourDeclines(0);
            setTourMode('tour');
          }}
          onDecline={() => {
            clearTourOfferPending();
            writeTourDeclines(readTourDeclines() + 1);
            setTourMode('idle');
          }}
        />
      )}
      {tourMode === 'tour' && (
        <GuidedTour
          steps={tourSteps}
          onFinish={() => setTourMode('idle')}
        />
      )}
    </div>
  );
}

export default Home;
